import { useEffect, type FC } from 'react';
import { observer } from 'mobx-react';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import type { ViewOptionsController } from '@/components/viewOption/ViewOptionsController';
import { ViewOptionName, type ViewOptionsGroup } from '@/engine/app/interfaces';
import { CollapsibleContent } from './CollapsibleContent';
import { viewOptionsStructureManager } from './viewOptionsStructure';
import { ViewOptionsContent } from './ViewOptionsContent';
import { useInjection } from 'inversify-react';
import type { ViewsService } from '@/engine/app/Views.service';
import { TYPES } from '@/engine/ioc/types';
import { useCollapsingState } from './useCollapsingState';

export const ViewOptionsTable: FC<{
  controller: ViewOptionsController;
}> = observer(({ controller }) => {
  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);

  const [collapsingState, toggleCollapse, resetCollapsingState] = useCollapsingState(
    viewsService.currentView?.getViewOptions()
  );

  useEffect(() => {
    resetCollapsingState(viewsService.currentView?.getViewOptions());
  }, [resetCollapsingState, viewsService.currentView]);

  const hasGroups = (controller.viewOptions?.at(0) as ViewOptionsGroup | undefined)?.children !== undefined;

  if (hasGroups) {
    return controller.viewOptions?.map((item, index) => {
      if (viewOptionsStructureManager.isGroup(item)) {
        const groupDisabledVisibility = !!item.disabledOptions?.includes(ViewOptionName.Visibility);

        return (
          !viewOptionsStructureManager.isGroupEmpty(item, controller) && (
            <TableContainer key={index} component={Paper} style={{ marginBottom: 10 }}>
              <Table data-testid={`view-options-table-${item.id}`} aria-label="caption table" size="small">
                <TableBody>
                  <CollapsibleContent
                    item={item}
                    controller={controller}
                    disabledVisibility={groupDisabledVisibility}
                    isCollapsed={collapsingState[item.id]}
                    toggleCollapse={(): void => toggleCollapse(item.id)}
                  >
                    <ViewOptionsContent
                      controller={controller}
                      items={item.children}
                      disabledVisibility={groupDisabledVisibility}
                    />
                  </CollapsibleContent>
                </TableBody>
              </Table>
            </TableContainer>
          )
        );
      }
    });
  } else {
    return (
      <TableContainer component={Paper}>
        <Table data-testid="view-options-table" aria-label="caption table" size="small">
          <TableBody>
            <ViewOptionsContent controller={controller} items={controller.viewOptions} />
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
});
